import { type Placement } from '@atlaskit/dropdown-menu/types';
import { MenuGroup, Section } from '@atlaskit/menu';
import { type TriggerProps } from '@atlaskit/popup';
import Popup, { type PopupProps } from '@databases/atlaskit/Popup';
import { useCommentStateContext } from '@databases/comments/hooks/useCommentContext';
import { DATABASE_CELL_CONTEXT_MENU_OPEN_ATTR } from '@databases/frontend-metrics/metrics/cell-context-menu';
import React, { type FunctionComponent, useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl-next';

import { isDrilldownMenu, DrilldownHeader, DrilldownWrapper } from './DrilldownMenu';
import MenuContentShell from './MenuContentShell';
import MenuItemRow from './MenuItemRow';
import ScrollGradientWrapper from './ScrollGradientWrapper';
import { type MenuData } from './contextual-menu-state';
import { useContextualMenu } from './hooks/useContextualMenu';
import { useDrilldownStack } from './hooks/useDrilldownStack';
import { MenuNavContext, type MenuPage } from './hooks/useMenuNav';
import { SharedSubmenuStateContext, useSubmenuState } from './hooks/useSubmenuState';
import { messages } from './messages';
import type { MenuItems } from './types';

export type { MenuItem } from './types';

const CONTEXTUAL_MENU_MIN_WIDTH = 248;

type NestedPopupProps = {
  footer?: React.ReactNode;
  items: MenuItems;
  onClose: (event?: Event | React.MouseEvent | React.KeyboardEvent) => void;
  initialPage?: MenuPage;
  /**
   * Optional data-* attributes spread onto the root `MenuGroup`. Used to stamp
   * cell-context-menu open markers consumed by the
   * `cell-context-menu-open-interaction-time` performance metric.
   */
  menuGroupDataAttributes?: Record<string, string>;
};

const NestedPopup: FunctionComponent<NestedPopupProps> = ({
  items,
  onClose,
  initialPage,
  footer,
  menuGroupDataAttributes,
}) => {
  const { formatMessage } = useIntl();
  const submenuState = useSubmenuState();
  const { activeSubmenuKey, closeAll: closeAllSubmenus } = submenuState;

  const {
    nav,
    currentPage,
    depth,
    isContentPage,
    backButtonRef,
    handleKeyDown,
    hasInitialPage,
  } = useDrilldownStack({ onClose, closeAllSubmenus, activeSubmenuKey, initialPage });

  // When a drilldown page provides structured items, show those instead of root items.
  const currentItems = useMemo(
    () => (isContentPage ? [] : currentPage?.items ?? items),
    [isContentPage, currentPage, items]
  );

  const { openThread } = useCommentStateContext();
  useEffect(() => {
    if (openThread.valueId) {
      onClose?.();
    }
  }, [onClose, openThread.valueId]);

  const nonEmptyGroups = useMemo(
    () => currentItems.filter((group) => group.items.length > 0),
    [currentItems]
  );

  const shouldShowSeparator = useCallback(
    (index: number) => {
      const menuGroup = nonEmptyGroups[index];
      return index > 0 && !menuGroup.hasNoSeparator;
    },
    [nonEmptyGroups]
  );

  const effectiveMinWidth = currentPage?.width ?? CONTEXTUAL_MENU_MIN_WIDTH;
  const hasDrilldownItems = items.some((group) =>
    group.items.some((item) => isDrilldownMenu(item.content))
  );
  const hasDrilldownActive = hasDrilldownItems || depth > 0;

  return (
    <MenuNavContext.Provider value={nav}>
      <SharedSubmenuStateContext.Provider value={submenuState}>
        <DrilldownWrapper active={hasDrilldownActive} onKeyDown={handleKeyDown}>
          <ScrollGradientWrapper>
            <MenuGroup
              minWidth={effectiveMinWidth}
              maxWidth={currentPage?.width}
              onClick={(e) => e.stopPropagation()}
              spacing="compact"
              data-contextual-menu="true"
              data-testid="database-context-menu"
              {...menuGroupDataAttributes}
            >
              {currentPage && currentPage.title && (
                <DrilldownHeader
                  title={currentPage.title}
                  showBackButton={!(hasInitialPage && depth === 1)}
                  onBack={() => nav.pop()}
                  onMouseEnter={closeAllSubmenus}
                  backButtonRef={backButtonRef as React.Ref<HTMLElement>}
                  backButtonLabel={formatMessage(
                    messages['contextual-menu.drilldown-back-button-label']
                  )}
                  headerAction={currentPage.headerAction}
                />
              )}

              {isContentPage && currentPage && currentPage.content && (
                <MenuContentShell>{currentPage.content(nav)}</MenuContentShell>
              )}

              {nonEmptyGroups.map((group, index) => (
                <Section
                  hasSeparator={shouldShowSeparator(index)}
                  title={group.sectionTitle}
                  key={index}
                >
                  {group.items?.map((item) => (
                    <MenuItemRow
                      key={item.key}
                      item={item}
                      nav={nav}
                      submenuState={submenuState}
                      onClose={onClose}
                    />
                  ))}
                </Section>
              ))}
              {footer}
            </MenuGroup>
          </ScrollGradientWrapper>
        </DrilldownWrapper>
      </SharedSubmenuStateContext.Provider>
    </MenuNavContext.Provider>
  );
};

export type ContextualMenuProps = Pick<
  PopupProps,
  'shouldRenderToParent' | 'shouldDisableFocusLock'
> & {
  menuData: MenuData;
  items?: MenuItems;
  trigger: (props: TriggerProps) => React.ReactNode;
  onClose?: (event?: Event | React.MouseEvent | React.KeyboardEvent | null) => void;
  offset?: [number, number];
  boundariesElement?: HTMLElement;
  placement?: Placement;
  shouldDisableFocusLock?: boolean;
  panel?: boolean;
  isActionBarEnabled?: boolean;
  rootPage?: MenuPage;
  footer?: React.ReactNode;
};

export const ContextualMenu: FunctionComponent<ContextualMenuProps> = ({
  menuData,
  trigger: Trigger,
  items = [{ items: [] }],
  onClose,
  offset,
  boundariesElement = document.body,
  placement = 'right-start',
  shouldRenderToParent,
  shouldDisableFocusLock,
  panel = false,
  isActionBarEnabled = false,
  rootPage,
  footer,
}) => {
  const { closeMenu, isMenuOpen, isPanel } = useContextualMenu(menuData);
  const handleCloseMenu = useCallback(
    (e?: Event | React.MouseEvent | React.KeyboardEvent | null) => {
      closeMenu();
      onClose?.(e);
    },
    [closeMenu, onClose]
  );
  const isPopupOpen = useMemo(
    () =>
      isMenuOpen &&
      (rootPage != null ||
        (items.length > 0 && items.some((item) => item.items.length > 0))),
    [isMenuOpen, items, rootPage]
  );

  const renderPopup = !isActionBarEnabled ? isPopupOpen && isPanel === panel : false;

  const menuGroupDataAttributes = useMemo(
    () =>
      menuData.type === 'value'
        ? {
            [DATABASE_CELL_CONTEXT_MENU_OPEN_ATTR]: '',
            'data-entry-id': menuData.entryId,
            'data-field-id': menuData.fieldId,
          }
        : undefined,
    [menuData]
  );

  return (
    <Popup
      role="menu"
      shouldDisableFocusLock={shouldDisableFocusLock}
      shouldRenderToParent={shouldRenderToParent}
      shouldFitViewport
      isOpen={renderPopup}
      onClose={handleCloseMenu}
      content={() => (
        <NestedPopup
          items={items}
          onClose={handleCloseMenu}
          initialPage={rootPage}
          footer={footer}
          menuGroupDataAttributes={menuGroupDataAttributes}
        />
      )}
      placement={placement}
      trigger={Trigger}
      offset={offset}
      boundary={boundariesElement}
      contentContainer={null}
      id="database-contextual-menu"
      data-testid="database-contextual-menu"
    />
  );
};

export default ContextualMenu;
